import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, Observable, of, take } from 'rxjs';
import {
    DeletePlatformLicensePricing,
    DeletePlatformLicensePricingFailure,
    DeletePlatformLicensePricingSuccess,
    LoadPlatformLicenseActivationsThatUseLicense,
    LoadPlatformLicenseActivationsThatUseLicenseSuccess,
    LoadPlatformLicensePricingsThatHaveNotBeenInvoiced,
    LoadPlatformLicensePricingsThatHaveNotBeenInvoicedFailure,
    LoadPlatformLicensePricingsThatHaveNotBeenInvoicedSuccess,
    LoadPlatformLicenses,
    LoadPlatformLicensesById,
    LoadPlatformLicensesByIdFailure,
    LoadPlatformLicensesByIdSuccess,
    LoadPlatformLicensesFailure,
    LoadPlatformLicensesSuccess,
    PlatformLicenseActionFailure,
    UpsertPlatformLicense,
    UpsertPlatformLicenseFailure,
    UpsertPlatformLicensePricing,
    UpsertPlatformLicensePricingSuccess,
    UpsertPlatformLicenseSuccess
} from '../actions/platform-license.actions';
import { PlatformLicenseControllerService, PlatformLicenseDTO, PlatformLicensePricingDTO } from '@iota/iota-management-portal-v4-client';
import { getLocationHeader } from 'src/app/shared/utils';

@Injectable()
export class PlatformLicenseEffects {
    loadPlatformLicenses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadPlatformLicenses),
            mergeMap(() =>
                this.apiService.getAllPlatformLicenses().pipe(
                    map((platformLicenses) => LoadPlatformLicensesSuccess({ platformLicenses })),
                    catchError((error) => of(LoadPlatformLicensesFailure({ error })))
                )
            )
        )
    );

    loadPlatformLicensesById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                LoadPlatformLicensesById,
                UpsertPlatformLicenseSuccess,
                UpsertPlatformLicensePricingSuccess,
                DeletePlatformLicensePricingSuccess
            ),
            mergeMap(({ platformLicenseId }) =>
                this.apiService.getPlatformLicense(platformLicenseId).pipe(
                    map((platformLicense) => LoadPlatformLicensesByIdSuccess({ platformLicense })),
                    catchError((error) => of(LoadPlatformLicensesByIdFailure({ error })))
                )
            )
        )
    );

    upsertPlatformLicense$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPlatformLicense),
            mergeMap(({ platformLicense }) =>
                this.createOrUpdatePlatformLicense(platformLicense).pipe(
                    map((platformLicenseId) => UpsertPlatformLicenseSuccess({ platformLicenseId })),
                    catchError((error) => of(UpsertPlatformLicenseFailure({ error: error })))
                )
            )
        )
    );

    upsertPlatformLicensePricing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPlatformLicensePricing),
            mergeMap(({ platformLicenseId, platformLicensePricing }) =>
                this.createOrUpdatePlatformLicensePricing(platformLicenseId, platformLicensePricing).pipe(
                    map(() => UpsertPlatformLicenseSuccess({ platformLicenseId })),
                    catchError((error) => of(UpsertPlatformLicenseFailure({ error: error })))
                )
            )
        )
    );

    deletePlatformLicensePricing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeletePlatformLicensePricing),
            mergeMap(({ platformLicenseId, platformLicensePricingId }) =>
                this.apiService.deletePlatformLicensePricing(platformLicenseId, platformLicensePricingId).pipe(
                    map(() => DeletePlatformLicensePricingSuccess({ platformLicenseId })),
                    catchError((error) => of(DeletePlatformLicensePricingFailure({ error: error })))
                )
            )
        )
    );

    loadPlatformLicensePricingsThatHaveNotBeenInvoiced$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadPlatformLicensePricingsThatHaveNotBeenInvoiced),
            mergeMap(({ platformLicenseId }) =>
                this.apiService.getAllPlatformLicensePricingIdsThatHaveNotBeenInvoiced(platformLicenseId).pipe(
                    map((platformLicensePricingIds) =>
                        LoadPlatformLicensePricingsThatHaveNotBeenInvoicedSuccess({
                            pricingIdsThatHaveNotBeenInvoiced: platformLicensePricingIds
                        })
                    ),
                    catchError((error) => of(LoadPlatformLicensePricingsThatHaveNotBeenInvoicedFailure({ error })))
                )
            )
        )
    );

    loadPlatformLicenseActivationsThatUseLicense$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadPlatformLicenseActivationsThatUseLicense),
            mergeMap(({ platformLicenseId }) =>
                this.apiService.getAllPlatformLicenseActivationsThatUseLicense(platformLicenseId).pipe(
                    take(1),
                    map((licenseActivations) =>
                        LoadPlatformLicenseActivationsThatUseLicenseSuccess({
                            platformLicenseId,
                            platformLicenseActivations: licenseActivations ?? []
                        })
                    ),
                    catchError((error) => of(PlatformLicenseActionFailure({ error })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private apiService: PlatformLicenseControllerService
    ) {}

    private createOrUpdatePlatformLicensePricing(
        platformLicenseId: string,
        platformLicensePricing: PlatformLicensePricingDTO
    ): Observable<any> {
        if (platformLicensePricing?.platformLicensePricingId) {
            return this.apiService.updatePlatformLicensePricing(
                platformLicensePricing?.platformLicenseId,
                platformLicensePricing?.platformLicensePricingId,
                platformLicensePricing
            );
        }

        return this.apiService.createPlatformLicensePricing(platformLicenseId, platformLicensePricing);
    }

    private createOrUpdatePlatformLicense(platformLicense: PlatformLicenseDTO): Observable<string> {
        if (platformLicense?.platformLicenseId) {
            return this.apiService
                .updatePlatformLicense(platformLicense?.platformLicenseId, platformLicense)
                .pipe(map(() => platformLicense?.platformLicenseId));
        }

        return this.apiService.createPlatformLicense(platformLicense, 'response').pipe(map((response) => getLocationHeader(response)));
    }
}
